import { getAdminOrgId } from "./admin";
import { extractApiErrorMessage, getApiBase, getAuthHeaders } from "./auth";

const API_BASE = getApiBase();

function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type QuoteStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "accepted"
  | "declined"
  | "expired"
  | "cancelled";

export type QuoteLineOut = {
  id: string;
  quote_id: string;
  quote_bundle_id: string | null;
  line_order: number;
  product_id: string | null;
  sku: string | null;
  label: string;
  product_type: string;
  ops_family: string | null;
  quantity: number;
  unit_kind: string | null;
  unit_price: number;
  total_price: number;
  tax_code: string | null;
  taxable_gst: boolean;
  taxable_qst: boolean;
  gst_amount: number;
  qst_amount: number;
  pricing_details: Record<string, unknown> | null;
  doc_detail_enabled: boolean;
  doc_detail_template_md: string | null;
  doc_detail_rendered_md: string | null;
  line_completion_status: string;
  created_at: string;
  updated_at: string;
};

export type QuoteTaxLineOut = {
  tax_code: string;
  rate: number;
  taxable_subtotal: number;
  amount: number;
};

export type QuoteBundleOut = {
  id: string;
  quote_id: string;
  bundle_product_id: string;
  name: string;
  notes: string | null;
  position: number;
  created_at: string;
  updated_at: string;
};

export type QuoteOut = {
  id: string;
  quote_number: string;
  org_id: string;
  client_id: string;
  status: QuoteStatus;
  valid_until: string | null;
  currency: string;
  subtotal: number;
  tax_total: number;
  total: number;
  credits_applied: number;
  total_after_credits: number;
  pricing_context: Record<string, unknown> | null;
  pricing_snapshot: Record<string, unknown> | null;
  notes_internal: string | null;
  notes_client: string | null;
  public_token: string;
  sent_at: string | null;
  viewed_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  declined_reason: string | null;
  billing_schedule: Record<string, unknown> | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  lines: QuoteLineOut[];
  tax_lines: QuoteTaxLineOut[];
  bundles: QuoteBundleOut[];
};

export type QuoteListResponse = {
  items: QuoteOut[];
  meta: { total: number; page: number; page_size: number };
};

export type QuoteLineAddPayload = {
  product_id?: string | null;
  label: string;
  product_type?: string;
  ops_family?: string | null;
  quantity?: number;
  unit_kind?: string | null;
  unit_price: number;
  description?: string | null;
  tax_code?: string | null;
  pricing_details?: Record<string, unknown> | null;
  line_order?: number | null;
};

export type QuoteLineUpdatePayload = {
  label?: string;
  quantity?: number;
  unit_kind?: string | null;
  unit_price?: number;
  description?: string | null;
  tax_code?: string | null;
  taxable_gst?: boolean;
  taxable_qst?: boolean;
  pricing_details?: Record<string, unknown> | null;
  line_order?: number;
};

export type QuoteBundleLineAdd = {
  product_id?: string | null;
  label: string;
  product_type?: string;
  ops_family?: string | null;
  quantity?: number;
  unit_kind?: string | null;
  unit_price: number;
  description?: string | null;
  tax_code?: string | null;
  pricing_details?: Record<string, unknown> | null;
};

export type QuoteBundleAddPayload = {
  bundle_product_id: string;
  name: string;
  notes?: string | null;
  lines: QuoteBundleLineAdd[];
};

export type QuoteCreatePayload = {
  org_id: string;
  client_id: string;
  valid_until?: string | null;
  currency?: string;
  notes_internal?: string | null;
  notes_client?: string | null;
  billing_schedule?: Record<string, unknown> | null;
  pricing_context?: Record<string, unknown> | null;
  lines?: QuoteLineAddPayload[];
};

export type QuoteUpdatePayload = {
  valid_until?: string | null;
  notes_internal?: string | null;
  notes_client?: string | null;
  billing_schedule?: Record<string, unknown> | null;
  pricing_context?: Record<string, unknown> | null;
};

export async function createQuote(payload: QuoteCreatePayload): Promise<QuoteOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de la création de la soumission"));
  }
  return response.json();
}

export async function getQuote(quoteId: string): Promise<QuoteOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec du chargement de la soumission"));
  }
  return response.json();
}

export async function listQuotes(params?: {
  status?: string;
  client_id?: string;
  query?: string;
  page?: number;
  page_size?: number;
}): Promise<QuoteListResponse> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append("status", params.status);
  if (params?.client_id) queryParams.append("client_id", params.client_id);
  if (params?.query) queryParams.append("q", params.query);
  if (params?.page != null) queryParams.append("page", params.page.toString());
  if (params?.page_size != null) queryParams.append("page_size", params.page_size.toString());
  const url = `${API_BASE}/quotes${queryParams.toString() ? `?${queryParams}` : ""}`;
  const response = await fetch(url, { headers: defaultHeaders(orgId) });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec du chargement des soumissions"));
  }
  return response.json();
}

export async function updateQuote(quoteId: string, payload: QuoteUpdatePayload): Promise<QuoteOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de la mise à jour de la soumission"));
  }
  return response.json();
}

export async function addQuoteLine(quoteId: string, payload: QuoteLineAddPayload): Promise<QuoteLineOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}/lines`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de l'ajout de la ligne"));
  }
  return response.json();
}

export async function updateQuoteLine(
  quoteId: string,
  lineId: string,
  payload: QuoteLineUpdatePayload
): Promise<QuoteLineOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}/lines/${lineId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de la mise à jour de la ligne"));
  }
  return response.json();
}

export async function deleteQuoteLine(quoteId: string, lineId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}/lines/${lineId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de la suppression de la ligne"));
  }
}

export async function addQuoteBundle(quoteId: string, payload: QuoteBundleAddPayload): Promise<QuoteOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}/bundles`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de l'ajout du bundle"));
  }
  return response.json();
}

export async function dissociateQuoteBundle(quoteId: string, bundleId: string): Promise<QuoteOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}/bundles/${bundleId}/dissociate`, {
    method: "POST",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de la dissociation du bundle"));
  }
  return response.json();
}

export async function computeQuote(quoteId: string): Promise<QuoteOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}/compute`, {
    method: "POST",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec du calcul de la soumission"));
  }
  return response.json();
}

export async function sendQuote(quoteId: string): Promise<QuoteOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/quotes/${quoteId}/send`, {
    method: "POST",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de l'envoi de la soumission"));
  }
  return response.json();
}
